/* Khách đang chờ — hàng chờ tư vấn tại cơ sở: khách check-in (Telesales, vãng lai, khách cũ) và phiếu chuyển từ CSKH */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, Modal, Radio, Segmented, Select, Switch, Table, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { CheckCircle2, Clock3, Inbox, MessagesSquare, Search, Shuffle, UserPlus } from 'lucide-react';
import { consultantOf } from '../Telesales/dashboard/data';
import { Avatar, Kpi } from '../Telesales/dashboard/ui';
import { MANGS, type Mang } from '../Telesales/lead/logic';
import {
  REASSIGN_MIN, STAFF_ST, WAIT_WARN_MIN, allCodes, calc, consultantsOf, dayStats, hm, isToday, minutes, nextCode, nextConsultant, uid, vnd,
  type Source, type Visit,
} from './logic';
import { useTuVan } from './store';
import { phoneKey } from './tele';
import { Card, DoctorGate, Shell, SourceTag, StatusTag, WaitText, startVisit, useMe, useNow, who } from './shared';

type Filter = 'waiting' | 'consulting' | 'referral' | 'done' | 'all';

interface WalkIn { name: string; phone: string; gender: 0 | 1; mang: Mang; need: string; source: Source }

export default function WaitingCustomersPage() {
  const navigate = useNavigate();
  const now = useNow();
  const me = useMe();
  const { visits, branchId, staffSt, autoAssign: autoOn, update, add, set, role } = useTuVan();
  const [filter, setFilter] = useState<Filter>('waiting');
  const [q, setQ] = useState('');
  const [walkIn, setWalkIn] = useState(false);
  const [form] = Form.useForm<WalkIn>();

  const today = useMemo(() => visits.filter((v) => v.branchId === branchId
    && (v.status === 'referral' || isToday(v.result?.at ?? v.checkInAt ?? v.createdAt, now))), [visits, branchId, now]);
  const by = (f: Filter) => today.filter((v) => f === 'all' || v.status === f);
  const waiting = by('waiting');
  const longest = waiting.reduce((m, v) => Math.max(m, now - (v.checkInAt ?? now)), 0);
  const referral = by('referral');
  const stats = dayStats(today, now);
  const cons = consultantsOf(branchId);

  const rows = by(filter)
    .filter((v) => !q.trim() || `${v.cust.name} ${v.cust.code} ${v.cust.phone} ${v.need}`.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => (a.status === 'done' ? (b.result?.at ?? 0) - (a.result?.at ?? 0) : (a.checkInAt ?? a.referral?.due ?? 0) - (b.checkInAt ?? b.referral?.due ?? 0)));

  const assign = (v: Visit, id?: string) => update(v.id, () => ({ consultant: id, assignedAt: id ? Date.now() : undefined }),
    { text: id ? `Phân khách cho ${who(id)}` : 'Bỏ phân khách', by: me.name });
  const open = (v: Visit, url = '/phieu-tu-van') => { set({ cur: v.id }); navigate(`${url}?v=${v.id}`); };
  const accept = (v: Visit) => {
    const id = v.consultant ?? me.id;
    if (!id) return;
    startVisit(v, id, me.name);
    open(v);
  };
  const checkIn = (v: Visit) => update(v.id, () => ({ status: 'waiting', checkInAt: Date.now() }), { text: 'Khách đến cơ sở · check-in theo phiếu CSKH', by: me.name });
  /** Phân khách chưa có người theo lượt: tư vấn viên sẵn sàng, đang rảnh, ít lượt nhất */
  const autoAssign = () => {
    const todo = waiting.filter((v) => !v.consultant).sort((a, b) => (a.checkInAt ?? 0) - (b.checkInAt ?? 0));
    let n = 0;
    todo.forEach((v) => {
      const id = nextConsultant(useTuVan.getState().visits.filter((x) => x.branchId === branchId), branchId, staffSt, Date.now());
      if (!id) return;
      assign(v, id);
      n += 1;
    });
    if (!todo.length) message.info('Không còn khách chờ chưa phân.');
    else if (!n) message.warning('Chưa có tư vấn viên rảnh và sẵn sàng.');
    else message.success(`Đã phân ${n} khách${n < todo.length ? ` · còn ${todo.length - n} khách chờ tư vấn viên rảnh` : ''}.`);
  };
  const saveWalkIn = async () => {
    const f = await form.validateFields();
    const v: Visit = {
      id: uid(), code: nextCode('LV', allCodes(visits), Date.now()), branchId, createdAt: Date.now(),
      cust: { name: f.name.trim(), code: `KH-${Date.now().toString().slice(-5)}`, phone: f.phone.trim(), gender: f.gender },
      phoneDigits: phoneKey(f.phone) || undefined,
      mang: f.mang, need: f.need.trim(), source: f.source, visitNo: f.source === 'Khách cũ' ? 2 : 1,
      checkInAt: Date.now(), status: 'waiting', note: '', deposits: [], plans: [],
      log: [{ at: Date.now(), text: `Check-in khách ${f.source === 'Vãng lai' ? 'vãng lai' : 'cũ'} tại quầy`, by: me.name }],
    };
    add([v]);
    setWalkIn(false);
    form.resetFields();
    setFilter('waiting');
    message.success(`Đã thêm ${v.cust.name} vào hàng chờ.`);
  };

  const columns: ColumnsType<Visit> = [
    {
      title: 'Khách', key: 'cust', fixed: 'left', width: 200,
      render: (_, v) => (
        <div className="min-w-0 leading-tight">
          <div className="truncate font-semibold text-gray-800 dark:text-gray-100">{v.cust.name}</div>
          <div className="mt-0.5 text-xs text-gray-500">{v.cust.code} · {v.cust.phone}{v.visitNo > 1 && <span className="ml-1 text-[#3e91ab]">· lần {v.visitNo}</span>}</div>
        </div>
      ),
    },
    {
      title: 'Check-in', key: 'in', width: 110,
      render: (_, v) => (v.status === 'referral'
        ? <div className="leading-tight"><div className="text-xs text-gray-500">Hạn nhận phiếu</div><div className={v.referral!.due < now ? 'font-semibold text-red-500' : ''}>{hm(v.referral!.due)}</div></div>
        : <div className="leading-tight"><div>{hm(v.checkInAt)}</div><div className="text-xs">{v.status === 'done' ? <span className="text-gray-400">chờ <WaitText v={v} now={now} /></span> : <WaitText v={v} now={now} />}</div></div>),
    },
    { title: 'Mảng · nhu cầu', key: 'need', width: 220, render: (_, v) => <span><b>{v.mang}</b> <span className="text-gray-400">·</span> {v.need}</span> },
    {
      title: 'Nguồn · báo giá online', key: 'src', width: 230,
      render: (_, v) => (
        <div className="flex flex-col items-start gap-0.5 text-xs leading-5">
          <span className="flex flex-wrap items-center gap-1.5"><SourceTag v={v} />{v.owner && <span className="text-gray-500">{v.owner}</span>}</span>
          {v.online && <span>{v.online.code} · từ <b>{vnd(v.online.total)}</b></span>}
          {v.onlineDeps?.map((d) => <span key={d.code} className={d.received ? 'text-green-600 dark:text-green-400' : 'text-amber-600'}>Cọc {vnd(d.amount)} · {d.status}</span>)}
        </div>
      ),
    },
    {
      title: 'Tư vấn viên', key: 'cons', width: 200,
      render: (_, v) => (v.status === 'waiting' || v.status === 'referral' ? (
        <div className="leading-tight">
          <Select size="small" allowClear placeholder={autoOn ? 'Chờ người rảnh' : 'Chưa phân'} value={v.consultant} onChange={(id) => assign(v, id)} className="w-full"
            options={cons.map((c) => ({ value: c.id, label: c.name }))} />
          {v.status === 'waiting' && v.consultant && v.assignedAt && (
            <div className="mt-1 text-[11px] text-gray-500">Chia {hm(v.assignedAt)}{autoOn && <> · tự chia lại lúc <b className={now - v.assignedAt > (REASSIGN_MIN - 3) * 60_000 ? 'text-amber-600' : ''}>{hm(v.assignedAt + REASSIGN_MIN * 60_000)}</b></>}</div>
          )}
        </div>
      ) : (
        <span className="inline-flex items-center gap-1.5">
          <Avatar name={who(v.consultant)} color={consultantOf(v.consultant)?.color} size={22} />
          <span className="leading-tight"><span className="block">{who(v.consultant)}</span>{v.status === 'consulting' && <span className="block text-xs text-gray-500">{v.room} · {hm(v.startedAt)}</span>}</span>
        </span>
      )),
    },
    {
      title: 'Trạng thái', key: 'st', width: 150,
      render: (_, v) => (
        <div className="flex flex-col items-start gap-0.5">
          <StatusTag v={v} />
          {v.status === 'done' && v.quote && <span className="text-xs text-gray-500">{vnd(calc(v.quote).total)}</span>}
        </div>
      ),
    },
    {
      title: '', key: 'act', fixed: 'right', width: 120, align: 'right',
      render: (_, v) => {
        if (v.status === 'waiting') return <Button type="primary" size="small" onClick={() => accept(v)} disabled={!v.consultant && !me.id}>Nhận khách</Button>;
        if (v.status === 'consulting') return <Button size="small" onClick={() => open(v)}>Mở phiếu</Button>;
        if (v.status === 'referral') return <Button size="small" onClick={() => checkIn(v)}>Khách đã đến</Button>;
        return <Button size="small" type="link" className="!px-0" onClick={() => open(v, '/ket-qua-tu-van')}>Xem kết quả</Button>;
      },
    },
  ];

  const count = (f: Filter) => by(f).length;
  const consulting = by('consulting');
  // Việc của tư vấn viên đang dùng máy: khách mình đang tư vấn, khách được chia chờ mình nhận
  const myNow = consulting.find((v) => v.consultant === me.id);
  const myNext = waiting.filter((v) => v.consultant === me.id);
  const mySt = me.id ? staffSt[me.id] ?? 'ready' : 'ready';
  const readyN = cons.filter((c) => (staffSt[c.id] ?? 'ready') === 'ready').length;

  // Tài khoản bác sĩ chỉ dùng bước Bác sĩ khám — báo rõ thay vì tự đẩy trang đi
  if (role === 'doctor') {
    return <Shell title="Khách đang chờ" sub="Hàng chờ tư vấn tại cơ sở"><DoctorGate>{null}</DoctorGate></Shell>;
  }

  return (
    <Shell title="Khách đang chờ" sub={`${dayjs(now).format('DD/MM/YYYY')} · khách check-in và phiếu chuyển từ CSKH — phân khách, nhận khách, theo dõi thời gian chờ`}>
      {me.id && (
        <section className="rounded-xl border border-[#3e91ab]/30 bg-[#3e91ab]/5 p-3 sm:p-4 dark:bg-[#3e91ab]/10">
          <div className="mb-2 flex items-center justify-between gap-2">
            <b className="text-[14px] text-gray-800 dark:text-gray-100">Việc của bạn</b>
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500"><span className="h-2 w-2 rounded-full" style={{ background: STAFF_ST[mySt].dot }} />{STAFF_ST[mySt].label}</span>
          </div>
          <div className="space-y-2">
            {myNow && (
              <div className="flex items-center gap-3 rounded-lg bg-white p-3 shadow-sm dark:bg-gray-800">
                <div className="min-w-0 flex-1 leading-snug">
                  <div className="text-xs text-[#3e91ab]">Đang tư vấn · {myNow.room} · từ {hm(myNow.startedAt)}</div>
                  <div className="truncate text-[15px] font-semibold text-gray-800 dark:text-gray-100">{myNow.cust.name}</div>
                  <div className="truncate text-xs text-gray-500">{myNow.mang} · {myNow.need}</div>
                </div>
                <Button type="primary" size="large" onClick={() => open(myNow)}>Mở phiếu</Button>
              </div>
            )}
            {myNext.map((v) => (
              <div key={v.id} className="rounded-lg border border-amber-200 bg-white p-3 shadow-sm dark:border-amber-500/30 dark:bg-gray-800">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1 leading-snug">
                    <div className="text-xs font-medium text-amber-700 dark:text-amber-400">Khách được chia cho bạn · chờ <WaitText v={v} now={now} /></div>
                    <div className="truncate text-[15px] font-semibold text-gray-800 dark:text-gray-100">{v.cust.name} <span className="text-xs font-normal text-gray-400">{v.cust.phone}</span></div>
                    <div className="line-clamp-2 text-xs text-gray-500"><b className="text-gray-700 dark:text-gray-300">{v.mang}</b> · {v.need}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs"><SourceTag v={v} />{v.online && <span className="text-gray-500">{v.online.code} · từ {vnd(v.online.total)}</span>}</div>
                  </div>
                </div>
                <Button type="primary" size="large" block className="mt-2" onClick={() => accept(v)}>Nhận khách</Button>
                {autoOn && v.assignedAt && <div className="mt-1 text-center text-[11px] text-gray-500">{now < v.assignedAt + REASSIGN_MIN * 60_000
                  ? <>Nhận trước <b className="text-amber-600">{hm(v.assignedAt + REASSIGN_MIN * 60_000)}</b> — quá giờ tự chia cho người khác</>
                  : <span className="text-red-500">Đã quá giờ nhận — khách sẽ chia cho người khác ngay khi có người rảnh</span>}</div>}
              </div>
            ))}
            {!myNow && !myNext.length && (
              <p className="text-[13px] text-gray-500 dark:text-gray-400">{mySt === 'ready'
                ? 'Bạn đang rảnh — khách check-in sẽ được chia tự động cho bạn.'
                : 'Bạn đang tạm nghỉ / ngoại tuyến nên không được chia khách. Chuyển “Sẵn sàng” ở góc trên để nhận khách.'}</p>
            )}
          </div>
        </section>
      )}

      {/* Điện thoại: số liệu gọn một hàng, chạm để lọc danh sách */}
      <div className="grid grid-cols-4 gap-1.5 sm:hidden">
        {([
          ['waiting', 'Chờ', waiting.length, longest >= WAIT_WARN_MIN * 60_000],
          ['consulting', 'Đang TV', consulting.length, false],
          ['referral', 'CSKH', referral.length, referral.some((v) => v.referral!.due < now)],
          ['done', 'Đã TV', stats.consulted, false],
        ] as [Filter, string, number, boolean][]).map(([f, label, n, alert]) => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={`rounded-lg border px-1 py-1.5 text-center ${filter === f ? 'border-[#3e91ab] bg-[#3e91ab]/10' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'}`}>
            <div className={`text-lg font-bold leading-6 ${alert ? 'text-red-500' : 'text-gray-800 dark:text-gray-100'}`}>{n}</div>
            <div className="text-[11px] text-gray-500">{label}</div>
          </button>
        ))}
      </div>
      <div className="hidden grid-cols-2 gap-3 sm:grid lg:grid-cols-4">
        <Kpi icon={<Clock3 size={15} />} label="Đang chờ" value={waiting.length} tone={longest >= WAIT_WARN_MIN * 60_000 ? 'red' : 'amber'}
          alert={longest >= WAIT_WARN_MIN * 60_000} active={filter === 'waiting'} onClick={() => setFilter('waiting')}
          sub={waiting.length ? <>Chờ lâu nhất <b>{minutes(longest)} phút</b> · {waiting.filter((v) => !v.consultant).length} chưa phân</> : 'Không có khách chờ'}
          hint={`Tô đỏ khi có khách chờ từ ${WAIT_WARN_MIN} phút chưa được nhận`} />
        <Kpi icon={<MessagesSquare size={15} />} label="Đang tư vấn" value={consulting.length} tone="sky" active={filter === 'consulting'} onClick={() => setFilter('consulting')}
          sub={consulting.length ? consulting.map((v) => who(v.consultant)).join(', ') : 'Chưa có'} />
        <Kpi icon={<Inbox size={15} />} label="Phiếu CSKH" value={referral.length} tone="indigo" active={filter === 'referral'} onClick={() => setFilter('referral')}
          alert={referral.some((v) => v.referral!.due < now)}
          sub={referral.length ? <>Hạn nhận gần nhất {hm(Math.min(...referral.map((v) => v.referral!.due)))}</> : 'Không có phiếu'}
          hint="Khách CSKH muốn tư vấn thêm dịch vụ — Tư vấn cơ sở nhận phiếu trong hạn (R21)" />
        <Kpi icon={<CheckCircle2 size={15} />} label="Đã tư vấn" value={stats.consulted} tone="green" active={filter === 'done'} onClick={() => setFilter('done')}
          sub={<>Chốt {stats.closed} · chưa chốt {stats.consulted - stats.closed} · tỉ lệ <b>{stats.rate}%</b></>}
          hint="Tỉ lệ chốt = khách chốt (thanh toán hoặc cọc) / khách đã ghi kết quả hôm nay" />
      </div>

      {/* Điện thoại: đội tư vấn thu gọn, chạm để xem */}
      <details className="rounded-xl border border-gray-200 bg-white px-3 py-2 sm:hidden dark:border-gray-700 dark:bg-gray-800">
        <summary className="cursor-pointer text-[13px] font-medium text-gray-700 dark:text-gray-200">
          Đội tư vấn · {readyN}/{cons.length} sẵn sàng · {consulting.length} đang tư vấn
        </summary>
        <div className="mt-2 divide-y divide-gray-100 dark:divide-gray-700">
          {cons.map((c) => {
            const st = STAFF_ST[staffSt[c.id] ?? 'ready'];
            const curV = today.find((v) => v.status === 'consulting' && v.consultant === c.id);
            const queued = waiting.filter((v) => v.consultant === c.id).length;
            return (
              <div key={c.id} className="flex items-center gap-2 py-2 text-[13px]">
                <Avatar name={c.name} color={c.color} size={26} />
                <div className="min-w-0 flex-1 leading-tight">
                  <div className="truncate font-medium">{c.name}{c.id === me.id && <span className="text-xs text-gray-400"> · bạn</span>}</div>
                  <div className="truncate text-xs text-gray-500">{curV ? `Đang tư vấn ${curV.cust.name}` : 'Đang rảnh'}{queued > 0 && ` · ${queued} chờ nhận`}</div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs text-gray-500"><span className="h-1.5 w-1.5 rounded-full" style={{ background: st.dot }} />{st.label}</span>
              </div>
            );
          })}
        </div>
      </details>

      <Card className="!hidden sm:!flex" title="Tư vấn viên hôm nay" extra={<span className="text-xs text-gray-500">{readyN}/{cons.length} sẵn sàng</span>}>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-2">
          {cons.map((c) => {
            const st = STAFF_ST[staffSt[c.id] ?? 'ready'];
            const curV = today.find((v) => v.status === 'consulting' && v.consultant === c.id);
            const queued = waiting.filter((v) => v.consultant === c.id).length;
            const s = dayStats(today, now, c.id);
            return (
              <div key={c.id} className="rounded-lg border border-gray-100 p-3 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <Avatar name={c.name} color={c.color} />
                  <div className="min-w-0 flex-1 leading-tight">
                    <div className="truncate font-medium text-gray-800 dark:text-gray-100">{c.name}</div>
                    <div className="flex items-center gap-1 text-[11px] text-gray-500"><span className="h-1.5 w-1.5 rounded-full" style={{ background: st.dot }} />{st.label}{c.id === me.id && ' · bạn'}</div>
                  </div>
                  <div className="text-right text-[11px] leading-tight text-gray-500"><b className="text-[13px] text-gray-800 dark:text-gray-100">{s.closed}/{s.consulted}</b><br />chốt</div>
                </div>
                <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                  {curV
                    ? <button type="button" className="text-left text-[#3e91ab] hover:underline" onClick={() => open(curV)}>Đang tư vấn {curV.cust.name} · {curV.room}</button>
                    : <span className="text-gray-400">Đang rảnh</span>}
                  {queued > 0 && <span className="text-amber-600"> · {queued} khách chờ nhận</span>}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{autoOn
          ? `Đang chia tự động: khách check-in vào người sẵn sàng, rảnh, ít lượt nhất; quá ${REASSIGN_MIN} phút chưa nhận thì chia lại cho người khác đang có mặt. Ai rời quầy thì chuyển trạng thái Tạm nghỉ ở góc trên.`
          : 'Đang tắt chia tự động — Trưởng phòng phân khách ở cột Tư vấn viên hoặc bấm “Phân theo lượt”.'}</p>
      </Card>

      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="max-w-full overflow-x-auto [scrollbar-width:none]">
            <Segmented value={filter} onChange={(x) => setFilter(x as Filter)} options={[
              { value: 'waiting', label: `Đang chờ (${count('waiting')})` },
              { value: 'consulting', label: `Đang tư vấn (${count('consulting')})` },
              { value: 'referral', label: `Phiếu CSKH (${count('referral')})` },
              { value: 'done', label: `Đã tư vấn (${count('done')})` },
              { value: 'all', label: 'Tất cả' },
            ]} />
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tên, mã khách, SĐT" value={q} onChange={(e) => setQ(e.target.value)} className="w-full sm:w-[220px]" />
            <Tooltip title={`Khách check-in tự chia cho tư vấn viên đang sẵn sàng, rảnh, ít lượt nhất. Quá ${REASSIGN_MIN} phút chưa nhận khách, hoặc tư vấn viên tạm nghỉ / ngoại tuyến → tự chia lại cho người khác.`}>
              <span className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-2.5 py-1 text-[13px] dark:border-gray-600">
                <Switch size="small" checked={autoOn} onChange={(x) => set({ autoAssign: x })} />Chia tự động
              </span>
            </Tooltip>
            <Tooltip title="Phân khách chưa có người cho tư vấn viên đang rảnh và sẵn sàng, ít lượt nhất trong ngày">
              <Button icon={<Shuffle size={14} />} onClick={autoAssign} className="!hidden sm:!inline-flex">Phân theo lượt</Button>
            </Tooltip>
            <Button type="primary" icon={<UserPlus size={14} />} onClick={() => setWalkIn(true)}>Thêm khách</Button>
          </div>
        </div>
        <div className="hidden md:block">
          <Table rowKey="id" size="middle" columns={columns} dataSource={rows} pagination={false} scroll={{ x: 1130 }}
            locale={{ emptyText: <div className="py-6 text-[13px] text-gray-400">{filter === 'waiting' ? 'Không có khách đang chờ' : 'Không có khách'}</div> }} />
        </div>
        {/* Điện thoại: mỗi khách một thẻ, nút thao tác rộng dễ bấm */}
        <div className="space-y-2 md:hidden">
          {rows.map((v) => (
            <div key={v.id} className={`rounded-xl border p-3 ${v.consultant === me.id && v.status !== 'done' ? 'border-[#3e91ab]/50 bg-[#3e91ab]/5' : 'border-gray-200 dark:border-gray-700'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 leading-tight">
                  <div className="truncate font-semibold text-gray-800 dark:text-gray-100">{v.cust.name}</div>
                  <div className="mt-0.5 text-xs text-gray-500">{v.cust.phone}{v.visitNo > 1 && <span className="text-[#3e91ab]"> · lần {v.visitNo}</span>}</div>
                </div>
                <div className="shrink-0 text-right text-xs leading-tight">
                  {v.status === 'referral'
                    ? <><div className="text-gray-500">Hạn nhận</div><b className={v.referral!.due < now ? 'text-red-500' : ''}>{hm(v.referral!.due)}</b></>
                    : <><div className="text-gray-500">Vào {hm(v.checkInAt)}</div><WaitText v={v} now={now} /></>}
                </div>
              </div>
              <div className="mt-1.5 line-clamp-2 text-[13px] text-gray-700 dark:text-gray-200"><b>{v.mang}</b> · {v.need}</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
                <SourceTag v={v} /><StatusTag v={v} />
                {v.online && <span className="text-gray-500">{v.online.code} · từ {vnd(v.online.total)}</span>}
                {v.status === 'done' && v.quote && <span className="text-gray-500">{vnd(calc(v.quote).total)}</span>}
              </div>
              {(v.status === 'waiting' || v.status === 'referral') ? (
                <div className="mt-2 flex items-center gap-2">
                  <Select allowClear placeholder={autoOn ? 'Chờ người rảnh' : 'Chưa phân'} value={v.consultant} onChange={(id) => assign(v, id)} className="min-w-0 flex-1"
                    options={cons.map((c) => ({ value: c.id, label: c.name }))} />
                  {v.status === 'waiting'
                    ? <Button type="primary" onClick={() => accept(v)} disabled={!v.consultant && !me.id}>Nhận khách</Button>
                    : <Button onClick={() => checkIn(v)}>Khách đã đến</Button>}
                </div>
              ) : (
                <div className="mt-2 flex items-center justify-between gap-2 text-xs text-gray-500">
                  <span className="inline-flex min-w-0 items-center gap-1.5"><Avatar name={who(v.consultant)} color={consultantOf(v.consultant)?.color} size={20} /><span className="truncate">{who(v.consultant)}{v.room && v.status === 'consulting' ? ` · ${v.room}` : ''}</span></span>
                  {v.status === 'consulting'
                    ? <Button onClick={() => open(v)}>Mở phiếu</Button>
                    : <Button type="link" className="!px-0" onClick={() => open(v, '/ket-qua-tu-van')}>Xem kết quả</Button>}
                </div>
              )}
              {v.status === 'waiting' && v.consultant && v.assignedAt && autoOn && (
                <div className="mt-1 text-[11px] text-gray-500">Chia {hm(v.assignedAt)} · tự chia lại lúc <b className={now - v.assignedAt > (REASSIGN_MIN - 3) * 60_000 ? 'text-amber-600' : ''}>{hm(v.assignedAt + REASSIGN_MIN * 60_000)}</b></div>
              )}
            </div>
          ))}
          {!rows.length && <div className="py-6 text-center text-[13px] text-gray-400">{filter === 'waiting' ? 'Không có khách đang chờ' : 'Không có khách'}</div>}
        </div>
      </Card>

      <Modal open={walkIn} title="Thêm khách vào hàng chờ" okText="Thêm" cancelText="Huỷ" onCancel={() => setWalkIn(false)} onOk={saveWalkIn} destroyOnHidden>
        <Form form={form} layout="vertical" initialValues={{ gender: 0, mang: 'Clinic', source: 'Vãng lai' }} className="pt-2">
          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
            <Form.Item name="name" label="Tên khách" rules={[{ required: true, whitespace: true, message: 'Nhập tên khách' }]}><Input placeholder="VD: Nguyễn Thị Lan" /></Form.Item>
            <Form.Item name="phone" label="Số điện thoại" rules={[{ required: true, pattern: /^0\d{9}$/, message: 'Số điện thoại 10 số, bắt đầu bằng 0' }]}><Input placeholder="09xxxxxxxx" /></Form.Item>
            <Form.Item name="gender" label="Xưng hô"><Radio.Group options={[{ value: 0, label: 'Chị' }, { value: 1, label: 'Anh' }]} /></Form.Item>
            <Form.Item name="source" label="Nguồn"><Radio.Group options={[{ value: 'Vãng lai', label: 'Vãng lai' }, { value: 'Khách cũ', label: 'Khách cũ' }]} /></Form.Item>
            <Form.Item name="mang" label="Mảng"><Select options={MANGS.map((m) => ({ value: m, label: m }))} /></Form.Item>
            <Form.Item name="need" label="Nhu cầu" rules={[{ required: true, whitespace: true, message: 'Nhập nhu cầu' }]}><Input placeholder="VD: nám, da không đều màu" /></Form.Item>
          </div>
        </Form>
        <p className="text-xs text-gray-500">Khách hẹn qua Telesales tự vào hàng chờ khi Lễ tân check-in lịch hẹn — không cần thêm tay.</p>
      </Modal>
    </Shell>
  );
}
